// Claims to review — in-memory mock store (CLAIMS_UI_BUILD_SPEC §13).
// No fetch, no PMS SDK, no clearinghouse. All async methods wait a mock latency
// then mutate in memory. Components subscribe for reactive reads.
import { useSyncExternalStore } from 'react';
import type {
  Claim,
  ClaimEditPatch,
  ClaimSummary,
  ConfirmResult,
  Mode,
  Patch,
  Settings,
} from './types';
import { buildAutopilotClaims, buildTrainingClaims } from './fixtures';
import { billedOf, CLAIMS_USER, clockNow, toSummary } from './utils';

export const LATENCY = {
  initial: 400,
  confirm: 900,
  save: 500,
  rewrite: 800,
  instant: 120,
} as const;

/** Mock aggregates shown on the milestone dialog (§11.1). */
export const MILESTONE_STATS = {
  changedCount: 31,
  changedLast50: 3,
  notReadyList: 'implants and Texas Medicaid',
};

type Scenario = '' | 'milestone' | 'autopilot' | 'empty' | 'writeback-fail';

interface State {
  claims: Claim[];
  settings: Settings;
  scenario: Scenario;
}

interface EffectMemo {
  attachmentId?: string;
  narrativeBefore?: string;
}

const listeners = new Set<() => void>();
let version = 0;
const effectMemo = new Map<string, EffectMemo>();
const failedOnce = new Set<string>();
const openedOnce = new Set<string>();

function emit(): void {
  version += 1;
  for (const l of listeners) l();
}

function readScenario(): Scenario {
  try {
    const raw = new URLSearchParams(window.location.search).get('scenario') ?? '';
    if (
      raw === 'milestone' ||
      raw === 'autopilot' ||
      raw === 'empty' ||
      raw === 'writeback-fail'
    ) {
      return raw;
    }
  } catch {
    /* no window (SSR) — default scenario */
  }
  return '';
}

function initState(): State {
  const scenario = readScenario();
  if (scenario === 'autopilot') {
    return {
      scenario,
      claims: buildAutopilotClaims(),
      settings: {
        mode: 'autopilot',
        confirmed: 200,
        milestoneShown: true,
        autopilotSince: 'Nov 3',
        autopilotBy: 'Ava Thompson',
      },
    };
  }
  if (scenario === 'empty') {
    return {
      scenario,
      claims: [],
      settings: { mode: 'training', confirmed: 47, milestoneShown: false },
    };
  }
  if (scenario === 'milestone') {
    return {
      scenario,
      claims: buildTrainingClaims(),
      settings: { mode: 'training', confirmed: 199, milestoneShown: false },
    };
  }
  return {
    scenario,
    claims: buildTrainingClaims(),
    settings: { mode: 'training', confirmed: 47, milestoneShown: false },
  };
}

const state: State = initState();

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function find(id: string): Claim | undefined {
  return state.claims.find((c) => c.id === id);
}

// ---- patch application (§13.3) ----------------------------------------------

function applyPatch(claim: Claim, patch: Patch): void {
  if (patch.procedures) {
    for (const edit of patch.procedures) {
      const proc = claim.procedures.find((p) => p.id === edit.id);
      if (!proc) continue;
      if (edit.code !== undefined) proc.code = edit.code;
      if (edit.description !== undefined) proc.description = edit.description;
      if ('tooth' in edit) proc.tooth = edit.tooth ?? null;
      if (edit.fee !== undefined) proc.fee = edit.fee;
      if ('elvaTouched' in edit) {
        if (edit.elvaTouched) proc.elvaTouched = true;
        else delete proc.elvaTouched;
      }
    }
  }
  if (patch.narrativeText !== undefined) {
    if (claim.narrative) {
      claim.narrative.text = patch.narrativeText;
      claim.narrative.state = patch.narrativeText ? 'elva' : 'empty';
    }
  }
  if (patch.addAttachment) {
    const add = patch.addAttachment;
    if (!claim.attachments.some((a) => a.id === add.id)) {
      claim.attachments.push({ ...add });
    }
  }
  if (patch.removeAttachmentId) {
    claim.attachments = claim.attachments.filter((a) => a.id !== patch.removeAttachmentId);
  }
  if (patch.payer) {
    claim.payer = { ...patch.payer };
  }
  if (patch.remarks !== undefined) {
    claim.remarks = patch.remarks;
  }
}

function pushHistory(claim: Claim, entry: string): void {
  claim.history = [...claim.history, entry];
}

const who = `${CLAIMS_USER.name} (${CLAIMS_USER.role})`;

// ---- reactive subscription --------------------------------------------------

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function getVersion(): number {
  return version;
}
export function useClaimsVersion(): number {
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

// ---- synchronous reactive getters ------------------------------------------

export function getModeSync(): Mode {
  return state.settings.mode;
}
export function getSettingsSync(): Settings {
  return { ...state.settings };
}
export function getScenarioSync(): Scenario {
  return state.scenario;
}

/** Claims still in the queue (not confirmed / not billed). */
function liveClaims(): Claim[] {
  return state.claims.filter((c) => !c.removed);
}

/** Waiting = live and not held (held excluded from counts, §1.2/§4.1). */
export function getWaitingClaimsSync(): Claim[] {
  return liveClaims().filter((c) => !c.held);
}

export function getWaitingCountSync(): number {
  return getWaitingClaimsSync().length;
}
/** Reactive hook for the sidebar badge. */
export function useWaitingCount(): number {
  return useSyncExternalStore(subscribe, getWaitingCountSync, getWaitingCountSync);
}
export function getWaitingTotalSync(): number {
  return getWaitingClaimsSync().reduce((sum, c) => sum + billedOf(c), 0);
}

export function getQueueSync(): ClaimSummary[] {
  const mode = state.settings.mode;
  return liveClaims().map((c) => toSummary(c, mode));
}

export function getClaimSync(id: string): Claim | null {
  return find(id) ?? null;
}

// ---- async interface (§13.1) -----------------------------------------------

export async function getQueue(): Promise<ClaimSummary[]> {
  await sleep(LATENCY.initial);
  return getQueueSync();
}

export async function getClaim(id: string): Promise<Claim | null> {
  await Promise.resolve();
  const claim = find(id);
  if (claim && !openedOnce.has(id) && !claim.removed) {
    openedOnce.add(id);
    pushHistory(claim, `opened by ${who} today`);
  }
  return claim ?? null;
}

export async function decide(id: string, optionId: string): Promise<Claim> {
  await Promise.resolve();
  const claim = find(id);
  if (!claim?.decision) throw new Error('no decision');
  const prev = claim.decision.chosen;
  if (prev && prev !== optionId) {
    // Reverse the previous effect first.
    const memo = effectMemo.get(id);
    if (memo?.attachmentId) {
      applyPatch(claim, { removeAttachmentId: memo.attachmentId });
    }
    if (memo?.narrativeBefore !== undefined) {
      applyPatch(claim, { narrativeText: memo.narrativeBefore });
    }
    effectMemo.delete(id);
  }
  const option = claim.decision.options.find((o) => o.id === optionId);
  if (!option) throw new Error('no option');
  const memo: EffectMemo = {};
  if (option.effect === 'attach' && option.attachment) {
    applyPatch(claim, { addAttachment: option.attachment });
    memo.attachmentId = option.attachment.id;
  } else if (option.effect === 'edit_narrative' && claim.narrative) {
    memo.narrativeBefore = claim.narrative.text;
    applyPatch(claim, { narrativeText: option.narrative ?? '' });
  }
  effectMemo.set(id, memo);
  claim.decision.chosen = optionId;
  pushHistory(claim, `decided by ${who} ${clockNow()}`);
  emit();
  return claim;
}

/** Set the chosen option without applying an effect (used by the Hold option). */
export function setDecisionChosen(id: string, optionId: string): void {
  const claim = find(id);
  if (claim?.decision) {
    claim.decision.chosen = optionId;
    emit();
  }
}

/** Reopen a resolved decision: reverse the current effect and clear chosen. */
export async function clearDecision(id: string): Promise<Claim> {
  await Promise.resolve();
  const claim = find(id);
  if (!claim?.decision) throw new Error('no decision');
  const memo = effectMemo.get(id);
  if (memo?.attachmentId) applyPatch(claim, { removeAttachmentId: memo.attachmentId });
  if (memo?.narrativeBefore !== undefined) applyPatch(claim, { narrativeText: memo.narrativeBefore });
  effectMemo.delete(id);
  delete claim.decision.chosen;
  emit();
  return claim;
}

export async function undoChange(id: string, changeId: string): Promise<Claim> {
  await Promise.resolve();
  const claim = find(id);
  if (!claim) throw new Error('no claim');
  const change = claim.changes.find((c) => c.id === changeId);
  if (!change) throw new Error('no change');
  applyPatch(claim, change.revert.before);
  change.applied = false;
  pushHistory(claim, `undid a change ${clockNow()}`);
  emit();
  return claim;
}

export async function redoChange(id: string, changeId: string): Promise<Claim> {
  await Promise.resolve();
  const claim = find(id);
  if (!claim) throw new Error('no claim');
  const change = claim.changes.find((c) => c.id === changeId);
  if (!change) throw new Error('no change');
  applyPatch(claim, change.revert.after);
  change.applied = true;
  pushHistory(claim, `redid a change ${clockNow()}`);
  emit();
  return claim;
}

export async function saveClaim(id: string, patch: ClaimEditPatch): Promise<Claim> {
  await sleep(LATENCY.save);
  const claim = find(id);
  if (!claim) throw new Error('no claim');
  if (patch.procedures) claim.procedures = patch.procedures.map((p) => ({ ...p }));
  if (patch.narrativeText !== undefined && claim.narrative) {
    claim.narrative.text = patch.narrativeText;
    if (patch.narrativeText && claim.narrative.state === 'empty') claim.narrative.state = 'edited';
  }
  if (patch.remarks !== undefined) claim.remarks = patch.remarks;
  if (patch.attachments) claim.attachments = patch.attachments.map((a) => ({ ...a }));
  pushHistory(claim, `edited by ${who} ${clockNow()}`);
  emit();
  return claim;
}

export async function confirmClaim(id: string): Promise<ConfirmResult> {
  await sleep(LATENCY.confirm);
  const claim = find(id);
  if (!claim) return { ok: false, error: 'writeback_failed' };
  if (state.scenario === 'writeback-fail' && !failedOnce.has(id)) {
    failedOnce.add(id);
    return { ok: false, error: 'writeback_failed' };
  }
  claim.removed = 'confirmed';
  if (state.settings.mode === 'training') {
    state.settings.confirmed += 1;
  }
  pushHistory(claim, `confirmed by ${who} ${clockNow()}`);
  emit();
  return { ok: true, confirmed: state.settings.confirmed };
}

export async function holdClaim(
  id: string,
  opts: { reason: string; note?: string; until?: string },
): Promise<void> {
  await Promise.resolve();
  const claim = find(id);
  if (!claim) return;
  claim.held = {
    reason: opts.reason,
    ...(opts.note ? { note: opts.note } : {}),
    ...(opts.until ? { until: opts.until } : {}),
  };
  pushHistory(claim, `held by ${who} ${clockNow()}`);
  emit();
}

export async function unholdClaim(id: string): Promise<void> {
  await Promise.resolve();
  const claim = find(id);
  if (!claim) return;
  // A hold for the dentist's approval comes off only when he approves (approveFinding).
  if (claim.findingApproval && claim.findingApproval.status !== 'approved') return;
  delete claim.held;
  pushHistory(claim, `took off hold ${clockNow()}`);
  emit();
}

/** "Ask Dr. Lee": the finding ELVA added goes to the dentist, who approves it on screen (D176). */
export async function askDentist(id: string): Promise<void> {
  await Promise.resolve();
  const claim = find(id);
  if (claim?.findingApproval?.status !== 'needed') return;
  claim.findingApproval = { ...claim.findingApproval, status: 'asked', askedAt: clockNow() };
  pushHistory(claim, `asked ${claim.findingApproval.dentist} to approve the finding ${clockNow()}`);
  emit();
}

/** The dentist typed his name on screen: the finding is approved and the hold comes off. */
export async function approveFinding(id: string): Promise<void> {
  await Promise.resolve();
  const claim = find(id);
  if (!claim?.findingApproval || claim.findingApproval.status === 'approved') return;
  claim.findingApproval = { ...claim.findingApproval, status: 'approved', approvedAt: clockNow() };
  delete claim.held;
  pushHistory(claim, `${claim.findingApproval.dentist} approved the finding ${clockNow()}`);
  emit();
}

export async function dontBill(
  id: string,
  _opts: { reason: string; other?: string },
): Promise<void> {
  await Promise.resolve();
  const claim = find(id);
  if (!claim) return;
  claim.removed = 'not_billed';
  pushHistory(claim, `marked not billed by ${who} ${clockNow()}`);
  emit();
}

export async function restoreClaim(id: string): Promise<void> {
  await Promise.resolve();
  const claim = find(id);
  if (!claim) return;
  delete claim.removed;
  emit();
}

export async function recordLearning(
  _id: string,
  _answer: 'right' | 'one_off',
): Promise<void> {
  await Promise.resolve();
  // Mock: nothing persists. Present for interface parity.

}

export async function getSettings(): Promise<Settings> {
  await Promise.resolve();
  return { ...state.settings };
}

export function markMilestoneShown(): void {
  state.settings.milestoneShown = true;
  emit();
}

export async function setMode(mode: Mode): Promise<void> {
  await Promise.resolve();
  if (mode === 'autopilot') {
    state.claims = buildAutopilotClaims();
    state.settings.mode = 'autopilot';
    state.settings.autopilotSince = state.settings.autopilotSince ?? 'Nov 3';
    state.settings.autopilotBy = state.settings.autopilotBy ?? 'Ava Thompson';
    openedOnce.clear();
  } else {
    state.settings.mode = 'training';
  }
  emit();
}
